/**
 * useEnquiriesSearchParams Hook
 * Provides deep linking for the enquiries page by syncing filter and sort
 * state with URL search parameters.
 *
 * Mirrors the pattern used in collins-ui (filterAsUrl directive + BookingsController)
 * where filters, sort order, pagination offset, and search query are serialised
 * as URL query parameters so that URLs can be shared/bookmarked.
 */

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EnquiriesSortOption,
  EnquiryStatus,
  FilterOptions
} from "../../types/enquiries";

const VALID_STATUSES: readonly EnquiryStatus[] = [
  "new",
  "in_progress",
  "complete",
  "cancelled",
  "lost",
  "deleted"
];

const VALID_SORT_OPTIONS: readonly EnquiriesSortOption[] = [
  "date_desc",
  "date_asc",
  "customer_asc",
  "customer_desc",
  "party_size_desc",
  "party_size_asc",
  "booking_value_desc",
  "booking_value_asc"
];

const DEFAULT_SORT: EnquiriesSortOption = "date_desc";

const MAX_QUERY_LENGTH = 200;

/**
 * Safely decodes a percent-encoded string, returning the original value
 * if the input contains malformed sequences that would cause a URIError.
 */
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export interface EnquiriesUrlState {
  status?: EnquiryStatus[];
  dateFrom?: string;
  dateTo?: string;
  assignedTo?: string[];
  sort: EnquiriesSortOption;
  offset: number;
  query: string;
}

/**
 * Parses and validates status values from a comma-separated URL parameter.
 * Invalid values are silently discarded.
 */
const parseStatusParam = (
  value: string | null
): EnquiryStatus[] | undefined => {
  if (!value) return undefined;

  const statuses = value
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is EnquiryStatus =>
      VALID_STATUSES.includes(s as EnquiryStatus)
    );

  return statuses.length > 0 ? statuses : undefined;
};

/**
 * Parses and validates the sort parameter from the URL.
 * Returns the default sort option if the value is invalid.
 */
const parseSortParam = (value: string | null): EnquiriesSortOption => {
  if (!value) return DEFAULT_SORT;

  return VALID_SORT_OPTIONS.includes(value as EnquiriesSortOption)
    ? (value as EnquiriesSortOption)
    : DEFAULT_SORT;
};

/**
 * Validates a date string is in YYYY-MM-DD format.
 * Returns the string if valid, otherwise undefined.
 */
const parseDateParam = (value: string | null): string | undefined => {
  if (!value) return undefined;

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) return undefined;

  const [, month, day] = value.split("-").map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;

  return value;
};

/**
 * Parses the offset (pagination start) parameter from the URL.
 * Returns 0 for invalid or negative values.
 */
const parseOffsetParam = (value: string | null): number => {
  if (!value) return 0;

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
};

/**
 * Validates an assigned_to parameter value.
 * Accepts a comma-separated list of 24-character hex strings (MongoDB
 * ObjectId format) or the special value 'me'. Invalid individual values
 * are silently discarded.
 */
const parseAssignedToParam = (value: string | null): string[] | undefined => {
  if (!value) return undefined;
  const mongoIdRegex = /^[0-9a-f]{24}$/;

  const ids = value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s === "me" || mongoIdRegex.test(s));

  return ids.length > 0 ? ids : undefined;
};

/**
 * Reads enquiries state from the current URL search parameters.
 */
export const getStateFromSearchParams = (search: string): EnquiriesUrlState => {
  const params = new URLSearchParams(search);

  return {
    status: parseStatusParam(params.get("status")),
    dateFrom: parseDateParam(params.get("date_from")),
    dateTo: parseDateParam(params.get("date_to")),
    assignedTo: parseAssignedToParam(params.get("assigned_to")),
    sort: parseSortParam(params.get("sort")),
    offset: parseOffsetParam(params.get("start")),
    query: (params.get("query") ?? "").slice(0, MAX_QUERY_LENGTH)
  };
};

/**
 * Serialises enquiries state into a URL search string.
 * Only non-default values are included to keep URLs clean.
 */
export const buildSearchString = (state: {
  filters: FilterOptions;
  sort: EnquiriesSortOption;
  offset: number;
  query: string;
}): string => {
  // Build URL params manually so that commas remain as literal ","
  // instead of being percent-encoded to "%2C" by URLSearchParams.
  // This matches the collins-ui convention (e.g. ?status=new,in_progress).
  const parts: string[] = [];

  if (state.filters.status?.length) {
    parts.push(`status=${state.filters.status.join(",")}`);
  }
  if (state.filters.date_from) {
    parts.push(`date_from=${encodeURIComponent(state.filters.date_from)}`);
  }
  if (state.filters.date_to) {
    parts.push(`date_to=${encodeURIComponent(state.filters.date_to)}`);
  }
  if (state.filters.assigned_to) {
    // Encode each individual ID but join with literal commas to keep URLs readable.
    const encodedIds = state.filters.assigned_to
      .split(",")
      .map((id) => encodeURIComponent(id.trim()))
      .join(",");
    parts.push(`assigned_to=${encodedIds}`);
  }
  if (state.sort !== DEFAULT_SORT) {
    parts.push(`sort=${encodeURIComponent(state.sort)}`);
  }
  if (state.offset > 0) {
    parts.push(`start=${state.offset}`);
  }
  if (state.query) {
    parts.push(`query=${encodeURIComponent(state.query)}`);
  }

  return parts.length > 0 ? `?${parts.join("&")}` : "";
};

/**
 * Checks whether the current URL contains any enquiries-specific search parameters.
 */
export const hasEnquiriesParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    params.has("status") ||
    params.has("date_from") ||
    params.has("date_to") ||
    params.has("assigned_to") ||
    params.has("sort") ||
    params.has("start") ||
    params.has("query")
  );
};

interface UseEnquiriesSearchParamsOptions {
  filters: FilterOptions;
  sortOption: EnquiriesSortOption;
  offset: number;
  searchQuery: string;
  onStateFromUrl: (state: EnquiriesUrlState) => void;
}

/**
 * Hook that provides bidirectional sync between enquiries state and URL params.
 *
 * On mount, if the URL contains enquiries params, the state is hydrated from them.
 * Subsequently, when state changes, the URL is updated using `replace` to avoid
 * polluting browser history with every filter toggle.
 */
export const useEnquiriesSearchParams = ({
  filters,
  sortOption,
  offset,
  searchQuery,
  onStateFromUrl
}: UseEnquiriesSearchParamsOptions): void => {
  const location = useLocation();
  const navigate = useNavigate();

  // Track whether we have completed the initial URL read to prevent
  // the write effect from running before the URL state is applied
  const initialisedRef = useRef(false);

  // Track the callback ref to avoid stale closures
  const onStateFromUrlRef = useRef(onStateFromUrl);
  onStateFromUrlRef.current = onStateFromUrl;

  // Flag URL changes that originate from the WRITE effect below so the
  // READ effect can skip them. Without this guard the two effects can
  // enter an infinite loop: WRITE updates the URL with stale state from
  // the current render → READ re-parses the (now incorrect) URL and
  // resets state → state change triggers WRITE again → loop.
  const isInternalNavigationRef = useRef(false);

  // READ: On mount (and on location changes such as back/forward navigation),
  // hydrate state from URL if params are present.
  useEffect(() => {
    // Skip URL changes that were triggered by the WRITE effect to
    // prevent re-applying (potentially stale) URL state.
    if (isInternalNavigationRef.current) {
      isInternalNavigationRef.current = false;
      return;
    }

    // Hydrate state from URL params, or reset to defaults when all params
    // have been removed (e.g. GlobalSearch clear navigating to /enquiries).
    const urlState = getStateFromSearchParams(location.search);
    if (hasEnquiriesParams(location.search) || initialisedRef.current) {
      onStateFromUrlRef.current(urlState);
    }
    initialisedRef.current = true;
  }, [location.search]);

  // WRITE: Keep URL in sync with state changes (after initialisation)
  useEffect(() => {
    if (!initialisedRef.current) return;

    const newSearch = buildSearchString({
      filters,
      sort: sortOption,
      offset,
      query: searchQuery
    });

    // Normalise both strings for comparison by decoding percent-encoded
    // characters and sorting params, so that "%2C" and "," are treated
    // the same and param order doesn't cause unnecessary navigation.
    const normalise = (s: string): string => {
      const withoutQ = s.startsWith("?") ? s.slice(1) : s;
      if (!withoutQ) return "";
      const parts = withoutQ
        .split("&")
        .map((p) => safeDecode(p))
        .sort((a, b) => a.localeCompare(b));
      return parts.length > 0 ? `?${parts.join("&")}` : "";
    };

    if (normalise(newSearch) !== normalise(location.search)) {
      isInternalNavigationRef.current = true;
      navigate({ search: newSearch }, { replace: true });
    }
  }, [filters, sortOption, offset, searchQuery, navigate, location.search]);
};
